import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiLock, FiMove, FiChevronUp, FiChevronDown,
  FiExternalLink, FiLayers, FiFilter, FiList,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  Card, DataTable, Button, Badge, Toggle, Modal, ConfirmDialog,
} from '../../components/common';
import CmsField from './components/CmsField';
import { useSortable } from './components/useSortable';
import { PAGE_TEMPLATES } from './sectionTypes';
import { pageService, getApiError, getApiFieldErrors } from '../../services/cmsService';
import { useDebounce } from '../../hooks';
import { slugify } from '../../utils/stringUtils';
import { formatDate } from '../../utils/dateUtils';
import './PagesListPage.css';

const PER_PAGE = 10;
const PUBLIC_SITE_URL = 'https://designinfra.co.in';

const emptyPage = () => ({
  title: '',
  slug: '',
  template: 'default',
  isActive: true,
  isSystem: false,
  metaTitle: '',
  metaDescription: '',
  metaKeywords: '',
  ogImage: '',
  canonicalUrl: '',
  sortOrder: 0,
});

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: '1', label: 'Published' },
  { value: '0', label: 'Draft' },
];

const PagesListPage = () => {
  const navigate = useNavigate();

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState(emptyPage());
  const [slugTouched, setSlugTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [reorderOpen, setReorderOpen] = useState(false);
  const [reorderList, setReorderList] = useState([]);
  const [reorderSaving, setReorderSaving] = useState(false);

  const debouncedSearch = useDebounce(search, 350);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await pageService.getAll({
        page,
        limit: PER_PAGE,
        search: debouncedSearch,
        status,
      });
      setPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to load pages'));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, status]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  // Any filter change resets to the first page so results are never orphaned
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status]);

  const openCreate = () => {
    setDraft(emptyPage());
    setSlugTouched(false);
    setFieldErrors({});
    setFormOpen(true);
  };

  const setDraftField = (field, value) => {
    setDraft((prev) => {
      const next = { ...prev, [field]: value };
      // Mirror the title into the slug until the user edits the slug themselves
      if (field === 'title' && !slugTouched) next.slug = slugify(value);
      return next;
    });
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleCreate = async () => {
    if (!draft.title.trim()) {
      setFieldErrors({ title: 'Page title is required' });
      return;
    }

    setSaving(true);
    try {
      const response = await pageService.create({ ...draft, slug: draft.slug || slugify(draft.title) });
      toast.success('Page created');
      setFormOpen(false);
      // Straight into the editor — unless the API returned no id, in which
      // case the list refresh is the honest fallback.
      if (response.data?.id) navigate(`/cms/pages/${response.data.id}`);
      else fetchPages();
    } catch (error) {
      const apiErrors = getApiFieldErrors(error);
      setFieldErrors({
        title: apiErrors.title,
        slug: apiErrors.slug,
        template: apiErrors.template,
      });
      toast.error(getApiError(error, 'Failed to create the page'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (row) => {
    const previous = pages;
    setPages((prev) => prev.map((item) => (item.id === row.id ? { ...item, isActive: !item.isActive } : item)));
    try {
      await pageService.toggleStatus(row.id);
    } catch (error) {
      setPages(previous);
      toast.error(getApiError(error, 'Failed to update status'));
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await pageService.delete(deleteTarget.id);
      toast.success('Page deleted');
      fetchPages();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to delete the page'));
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const openReorder = async () => {
    setReorderOpen(true);
    try {
      // Reordering is global, so pull the full list rather than the current page
      const response = await pageService.getAll({ page: 1, limit: 200 });
      setReorderList(response.data.pages);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to load pages for reordering'));
      setReorderOpen(false);
    }
  };

  const saveOrder = async () => {
    setReorderSaving(true);
    try {
      await pageService.reorder(reorderList.map((item) => item.id));
      toast.success('Page order saved');
      setReorderOpen(false);
      fetchPages();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to save the new order'));
    } finally {
      setReorderSaving(false);
    }
  };

  const { move, getItemProps, getItemClass } = useSortable(reorderList, setReorderList);

  const columns = [
    {
      header: 'Page',
      render: (row) => (
        <button type="button" className="pl-page-cell" onClick={() => navigate(`/cms/pages/${row.id}`)}>
          <span className="pl-page-icon"><FiLayers /></span>
          <span className="pl-page-text">
            <span className="pl-page-title">
              {row.title}
              {row.isSystem && (
                <span className="pl-system" title="System page — slug is fixed and it cannot be deleted">
                  <FiLock />
                </span>
              )}
            </span>
            <span className="pl-page-slug">/{row.slug}</span>
          </span>
        </button>
      ),
    },
    {
      header: 'Template',
      width: '130px',
      render: (row) => <Badge variant="default" size="small">{row.template || 'default'}</Badge>,
    },
    {
      header: 'Sections',
      width: '100px',
      align: 'center',
      render: (row) => (row.sectionsCount == null ? <span className="pl-muted">—</span> : row.sectionsCount),
    },
    {
      header: 'Status',
      width: '150px',
      render: (row) => (
        <div className="pl-status">
          <Toggle name={`page-status-${row.id}`} checked={row.isActive} onChange={() => handleToggleStatus(row)} size="small" />
          <Badge variant={row.isActive ? 'success' : 'warning'} size="small">
            {row.isActive ? 'Published' : 'Draft'}
          </Badge>
        </div>
      ),
    },
    {
      header: 'Updated',
      width: '130px',
      render: (row) => <span className="pl-muted">{row.updatedAt ? formatDate(row.updatedAt) : '—'}</span>,
    },
    {
      header: 'Actions',
      width: '140px',
      align: 'center',
      render: (row) => (
        <div className="pl-actions">
          <button type="button" className="action-btn edit" title="Edit content" onClick={() => navigate(`/cms/pages/${row.id}`)}>
            <FiEdit2 />
          </button>
          <a
            className="action-btn"
            href={`${PUBLIC_SITE_URL}/${row.slug === 'home' ? '' : row.slug}`}
            target="_blank"
            rel="noreferrer"
            title="View on the website"
          >
            <FiExternalLink />
          </a>
          <button
            type="button"
            className="action-btn delete"
            title={row.isSystem ? 'System pages cannot be deleted' : 'Delete page'}
            disabled={row.isSystem}
            onClick={() => setDeleteTarget(row)}
          >
            <FiTrash2 />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="pages-list-page">
      <Card
        title="Website Pages"
        subtitle={`${total} ${total === 1 ? 'page' : 'pages'} · edit the content that appears on designinfra.co.in`}
        action={
          <div className="pl-header-actions">
            <Button variant="outline" icon={<FiList />} onClick={openReorder}>
              Reorder
            </Button>
            <Button icon={<FiPlus />} onClick={openCreate}>
              New Page
            </Button>
          </div>
        }
      >
        <DataTable
          columns={columns}
          data={pages}
          loading={loading}
          searchable
          searchPlaceholder="Search pages by title or slug…"
          searchValue={search}
          onSearch={setSearch}
          pagination
          pageSize={PER_PAGE}
          totalItems={total}
          currentPage={page}
          onPageChange={setPage}
          emptyMessage="No pages found. Create one to get started."
          actions={
            <div className="pl-filter">
              <FiFilter />
              <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          }
        />
      </Card>

      {/* Create page */}
      <Modal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        title="New Page"
        size="large"
        footer={
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} loading={saving}>Create &amp; edit content</Button>
          </>
        }
      >
        <div className="pl-form">
          <div className="pl-form-grid">
            <CmsField
              type="text"
              label="Page Title"
              required
              placeholder="e.g. About Us"
              value={draft.title}
              onChange={(value) => setDraftField('title', value)}
              error={fieldErrors.title}
            />
            <CmsField
              type="text"
              label="Slug"
              placeholder="about-us"
              value={draft.slug}
              onChange={(value) => {
                setSlugTouched(true);
                setDraftField('slug', slugify(value));
              }}
              hint="The URL path. Left blank, it is generated from the title."
              error={fieldErrors.slug}
            />
            <CmsField
              type="select"
              label="Template"
              value={draft.template}
              options={PAGE_TEMPLATES}
              onChange={(value) => setDraftField('template', value)}
              hint="Tells the website which layout to render."
              error={fieldErrors.template}
            />
            <CmsField
              type="number"
              label="Sort Order"
              value={draft.sortOrder}
              onChange={(value) => setDraftField('sortOrder', value)}
            />
          </div>

          <details className="pl-seo">
            <summary>SEO &amp; social (optional)</summary>
            <div className="pl-form-stack">
              <CmsField
                type="text"
                label="Meta Title"
                placeholder="About Us | Design &amp; Infra Engineering"
                value={draft.metaTitle}
                onChange={(value) => setDraftField('metaTitle', value)}
                hint="Around 60 characters shows in full on Google."
              />
              <CmsField
                type="textarea"
                label="Meta Description"
                rows={3}
                placeholder="A short summary shown in search results."
                value={draft.metaDescription}
                onChange={(value) => setDraftField('metaDescription', value)}
              />
              <CmsField
                type="image"
                label="Social Share Image"
                value={draft.ogImage}
                onChange={(value) => setDraftField('ogImage', value)}
              />
            </div>
          </details>

          <div className="pl-form-footer">
            <Toggle
              name="page-active"
              checked={draft.isActive}
              onChange={(e) => setDraftField('isActive', e.target.checked)}
              label={draft.isActive ? 'Published' : 'Draft'}
            />
          </div>
        </div>
      </Modal>

      {/* Reorder */}
      <Modal
        isOpen={reorderOpen}
        onClose={() => setReorderOpen(false)}
        title="Reorder Pages"
        size="medium"
        footer={
          <>
            <Button variant="ghost" onClick={() => setReorderOpen(false)}>Cancel</Button>
            <Button onClick={saveOrder} loading={reorderSaving} disabled={!reorderList.length}>Save order</Button>
          </>
        }
      >
        <p className="pl-reorder-hint">Drag a page, or use the arrows, to set the order used by menus and listings.</p>
        <ul className="pl-reorder-list">
          {reorderList.map((item, index) => (
            <li key={item.id} className={`pl-reorder-row ${getItemClass(index)}`} {...getItemProps(index)}>
              <span className="pl-reorder-handle"><FiMove /></span>
              <span className="pl-reorder-index">{index + 1}</span>
              <span className="pl-reorder-text">
                <span className="pl-reorder-title">{item.title}</span>
                <span className="pl-reorder-slug">/{item.slug}</span>
              </span>
              <span className="pl-reorder-actions">
                <button type="button" className="action-btn" title="Move up" disabled={index === 0} onClick={() => move(index, index - 1)}>
                  <FiChevronUp />
                </button>
                <button type="button" className="action-btn" title="Move down" disabled={index === reorderList.length - 1} onClick={() => move(index, index + 1)}>
                  <FiChevronDown />
                </button>
              </span>
            </li>
          ))}
        </ul>
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Page"
        message={`Delete "${deleteTarget?.title}"? Its sections and items are deleted with it. This cannot be undone.`}
        confirmText="Delete page"
        loading={deleting}
      />
    </div>
  );
};

export default PagesListPage;
